import { useRoleTagMatrix, useToggleRoleTag } from '../../hooks/useCapacity';
import { useNavigate } from 'react-router-dom';
import { Spinner } from '../../components/common/Spinner';

export default function UserRoleMatrixPage() {
  const navigate = useNavigate();
  const { data: matrixData, isLoading } = useRoleTagMatrix();
  const toggleMutation = useToggleRoleTag();

  if (isLoading) return <div className="flex justify-center py-8"><Spinner /></div>;

  const { users, roles, tags } = matrixData?.data ?? { users: [], roles: [], tags: [] };
  const tagSet = new Set(tags.map((t) => `${t.userId}-${t.roleId}`));

  return (
    <div className="space-y-6">
      <div>
        <button onClick={() => navigate('/admin')} className="text-sm text-gray-500 hover:text-gray-700 mb-1">
          &larr; Back to Admin
        </button>
        <h1 className="text-2xl font-semibold text-gray-900">User Role Matrix</h1>
        <p className="text-sm text-gray-500 mt-1">Assign project roles to users. Click a checkbox to toggle.</p>
      </div>

      <div className="overflow-auto rounded-lg border border-gray-200 bg-white" style={{ maxHeight: 'calc(100vh - 200px)' }}>
        <table className="divide-y divide-gray-200">
          <thead className="bg-gray-50 sticky top-0 z-20">
            <tr>
              <th className="sticky left-0 z-30 bg-gray-50 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-[200px] min-w-[200px]">User</th>
              {roles.map((role) => (
                <th key={role.id} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                  {role.code ? `${role.code}` : role.name}
                  {role.code && <span className="block font-normal normal-case text-gray-400">{role.name}</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {users.map((user, idx) => (
              <tr key={user.id} className={idx % 2 === 1 ? 'bg-gray-50 hover:bg-gray-100' : 'hover:bg-gray-50'}>
                <td className={`sticky left-0 z-10 px-4 py-2 text-sm font-medium text-gray-900 whitespace-nowrap w-[200px] min-w-[200px] ${idx % 2 === 1 ? 'bg-gray-50' : 'bg-white'}`}>
                  {[user.lastName, user.firstName].filter(Boolean).join(' ') || user.email}
                </td>
                {roles.map((role) => {
                  const isTagged = tagSet.has(`${user.id}-${role.id}`);
                  return (
                    <td key={role.id} className="px-4 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={isTagged}
                        onChange={() => {
                          // F-1407: optimistic update is handled inside
                          // useToggleRoleTag (onMutate writes to query cache),
                          // so the checkbox re-renders automatically. No more
                          // direct Set mutation needed here.
                          toggleMutation.mutate({ userId: user.id, roleId: role.id });
                        }}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
